import { useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/query-keys';

export interface PageModuleContext {
  moduleType: 'ENTITY' | 'PROJECT' | 'TASK';
  moduleId: string;
  moduleName: string | null;
}

/**
 * Detects if the user is on a page that represents a linkable module (entity, project, or task).
 * Reads from React Query cache only — does NOT trigger new API fetches.
 * Returns null if not on a recognized module page or if data isn't cached.
 */
export function usePageModuleContext(): PageModuleContext | null {
  const params = useParams<{
    entityId?: string;
    projectId?: string;
    taskIdentifier?: string;
  }>();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  // Entity page: /:orgSlug/entities/:entityId/*
  if (params.entityId && pathname.includes('/entities/')) {
    const cached = queryClient.getQueryData<{ name?: string }>(
      queryKeys.entities.detail(params.entityId)
    );
    return {
      moduleType: 'ENTITY',
      moduleId: params.entityId,
      moduleName: cached?.name ?? null,
    };
  }

  // Project page: /:orgSlug/projects/:projectId/*
  if (params.projectId && pathname.includes('/projects/')) {
    const cached = queryClient.getQueryData<{ name?: string }>(
      queryKeys.projects.detail(params.projectId)
    );
    return {
      moduleType: 'PROJECT',
      moduleId: params.projectId,
      moduleName: cached?.name ?? null,
    };
  }

  // Task page: /:orgSlug/tasks/:taskIdentifier
  if (params.taskIdentifier && pathname.includes('/tasks/')) {
    // taskIdentifier can be UUID or task number (e.g., "YOLK-123")
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      params.taskIdentifier
    );

    if (isUuid) {
      const cached = queryClient.getQueryData<{ id: string; name?: string }>(
        queryKeys.tasks.detail(params.taskIdentifier)
      );
      if (cached) {
        return {
          moduleType: 'TASK',
          moduleId: cached.id,
          moduleName: cached.name ?? null,
        };
      }
    } else {
      // Task number lookup — check byNumber cache
      const cached = queryClient.getQueryData<{ id: string; name?: string }>(
        queryKeys.tasks.byNumber(params.taskIdentifier)
      );
      if (cached) {
        return {
          moduleType: 'TASK',
          moduleId: cached.id,
          moduleName: cached.name ?? null,
        };
      }
    }

    // Task param exists but data not cached yet
    return null;
  }

  return null;
}
